import { canv } from "@/canv/global";

import { logException } from "@/canv/utils";
import type { ExtendsOfElementBase } from "@/canvasController/types";
import type { CommentMessage, CommentMessageRemove } from "@/elements/comment/types";
import { BuildEvent } from "@/eventManager/events/buildEvent";
import { PropertyBuildEvent } from "@/eventManager/events/propertyBuildEvent";
import { PropertyEvent } from "@/eventManager/events/propertyEvent";
import { TouchEvent } from "@/eventManager/events/touchEvent";
import type { ArrowData } from "@/group/types";
import type { EntityBaseState } from "@/shared/types/entityState";
import type { FixType } from "@/shared/types/fixType";
import type { EventStructure, PropertyBuildEventAction } from "./events/types";
import type { Rail, RecordEvent } from "./types";

export class EventManager {
    uBuf: RecordEvent[][];
    rBuf: RecordEvent[][];
    currentRecord: RecordEvent[];
    currentTwinRecord: RecordEvent[];
    needInitWidgets: ExtendsOfElementBase[];
    rails: Rail[];
    uBufCapacity: number;

    constructor() {
        this.uBuf = [];
        this.rBuf = [];
        this.currentRecord = [];
        this.currentTwinRecord = [];

        this.needInitWidgets = [];
        this.rails = [];

        this.uBufCapacity = 100;
    }

    // new events

    createBuildEvent(action, group) {
        const states = [];
        group.collectEntitiesStates(states);

        const event = new BuildEvent(action, states);

        const twinAction = action === "create" ? "delete" : "create";
        const twinEvent = new BuildEvent(twinAction, states);

        this.currentRecord.push(event);
        this.currentTwinRecord.unshift(twinEvent);
    }

    createPropertyEvent(
        UID: string,
        fixType: FixType,
        beforeState: EntityBaseState,
        afterState: EntityBaseState,
    ) {
        const event = new PropertyEvent(UID, fixType, afterState);

        const twinEvent = new PropertyEvent(UID, fixType, beforeState);

        this.currentRecord.push(event);
        this.currentTwinRecord.unshift(twinEvent);
    }

    createPropertyBuildEvent(
        UID: string,
        fixType: FixType,
        action: PropertyBuildEventAction,
        property: ArrowData | CommentMessage | CommentMessageRemove,
    ) {
        const event = new PropertyBuildEvent(UID, fixType, action, property);

        this.currentRecord.push(event);
        this.currentTwinRecord.unshift(event.getTwin());
    }

    createTouchEvent(UID: string) {
        const event = new TouchEvent(UID);

        this.currentRecord.push(event);
        this.currentTwinRecord.unshift(event.getTwin());
    }

    // -------------------------------------

    clear() {
        this.uBuf = [];
        this.rBuf = [];

        this.currentRecord = [];
        this.currentTwinRecord = [];

        this.needInitWidgets = [];
        this.rails = [];
    }

    openRecord() {
        this.currentRecord = [];
    }

    closeRecord() {
        if (this.currentRecord.length === 0) {
            return;
        }

        this.addRail(this.currentRecord, this.currentTwinRecord);

        this.uBuf.push(this.currentTwinRecord);

        canv.intercom.eventMessage(this.getRecordToSend(this.currentRecord));

        this.rBuf = [];

        if (this.uBuf.length >= this.uBufCapacity) {
            this.uBuf.splice(0, 1);
        }

        this.currentRecord = [];
        this.currentTwinRecord = [];
    }

    getRecordToSend(record: RecordEvent[]) {
        const resultRecord: EventStructure[] = [];
        for (let i = 0; i < record.length; i++) {
            resultRecord.push(record[i].getEvent());
        }

        return resultRecord;
    }

    initWidgets() {
        for (let i = 0; i < this.needInitWidgets.length; i++) {
            this.needInitWidgets[i].initWidget();
        }
        this.needInitWidgets = [];
    }

    applyTransaction(record: RecordEvent[], addRail = false, buf?: RecordEvent[][]) {
        const twinRecord: RecordEvent[] = [];
        let allDone = true;

        for (let i = 0; i < record.length; i++) {
            if (!record[i].checkEvent()) {
                allDone = false;
                break;
            } else {
                const twinEvent = record[i].getTwin();
                record[i].execEvent();
                twinRecord.unshift(twinEvent);
            }
        }

        if (!allDone) {
            for (let i = 0; i < twinRecord.length; i++) {
                twinRecord[i].execEvent();
            }
            return false;
        }

        if (buf) {
            buf.push(twinRecord);
        }

        this.initWidgets();

        if (addRail) {
            this.addRail(record, twinRecord);
        }

        return true;
    }

    // Undo redo exec

    Undo() {
        try {
            if (this.uBuf.length === 0) {
                return;
            }

            const record = this.uBuf[this.uBuf.length - 1];
            const isDone = this.applyTransaction(record, true, this.rBuf);

            this.uBuf.splice(this.uBuf.length - 1, 1);

            if (isDone) {
                canv.intercom.eventMessage(this.getRecordToSend(record));
            }

            canv.widgetMenu.recollectWidgetOptionsMap();
            canv.redraw();
        } catch (error) {
            if (error instanceof Error) {
                logException(error);
            }
        }
    }

    Redo() {
        try {
            if (this.rBuf.length === 0) {
                return;
            }

            const record = this.rBuf[this.rBuf.length - 1];
            const isDone = this.applyTransaction(record, true, this.uBuf);

            this.rBuf.splice(this.rBuf.length - 1, 1);

            if (isDone) {
                canv.intercom.eventMessage(this.getRecordToSend(record));
            }

            canv.widgetMenu.recollectWidgetOptionsMap();
            canv.redraw();
        } catch (error) {
            if (error instanceof Error) {
                logException(error);
            }
        }
    }

    //Функция для undo/redo ai-функционала
    /* createAndInsertAiTwinRecord(beforeStates: EntityBaseState[], afterStates: EntityBaseState[]) {
        const twinRecord: PropertyEvent[] = [];

        for (const state of afterStates) {
            const twinEvent = new PropertyEvent(state.UID, state.fixType, beforeStates[state.UID]);
            twinRecord.push(twinEvent);
        }

        twinRecord.reverse();
        this.uBuf.push(twinRecord);
    } */

    createEventFromState(state: EventStructure) {
        switch (state.type) {
            case "build":
                return new BuildEvent(state.action, state.entities);
            case "propertyBuild":
                return new PropertyBuildEvent(
                    state.UID,
                    state.fixType,
                    state.action,
                    state.property,
                );
            case "property":
                return new PropertyEvent(state.UID, state.fixType, state.state);
            case "touch":
                return new TouchEvent(state.UID);
        }
    }

    processIncomingRecord(record: EventStructure[]) {
        this.revertRails();

        const eventsRecord: RecordEvent[] = [];

        for (const eventState of record) {
            const event = this.createEventFromState(eventState);
            eventsRecord.push(event);
        }

        this.applyTransaction(eventsRecord);
    }

    addRail(original: RecordEvent[], twin: RecordEvent[]) {
        const data: Rail = {
            status: "applied",
            original: original,
            twin: twin,
        };

        this.rails.push(data);
    }

    getTwinRecord(record: RecordEvent[]) {
        const twinRecord: RecordEvent[] = [];

        for (let i = 0; i < record.length; i++) {
            const twinEvent = record[i].getTwin(); // важно взять его до apply
            twinRecord.unshift(twinEvent);
        }

        return twinRecord;
    }

    revertRails() {
        for (let i = this.rails.length - 1; i >= 0; i--) {
            const rail = this.rails[i];

            if (rail.status !== "applied") {
                break;
            }

            this.applyTransaction(rail.twin);

            rail.status = "reverted";
        }
    }

    processEventConfirm(isApplied: boolean) {
        if (this.rails.length === 0) return;

        if (isApplied) {
            if (this.rails[0].status === "reverted") {
                this.revertRails();
                this.applyTransaction(this.rails[0].original);
                this.rails.splice(0, 1);
            } else {
                this.rails.splice(0, 1);
            }
        } else {
            if (this.rails[0].status === "reverted") {
                this.rails.splice(0, 1);
            } else {
                this.revertRails();
                this.rails.splice(0, 1);
            }
        }
        canv.redraw();
    }
}
